import { useEffect, useRef } from "react";
import request from "@/lib/api/handler";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../utils";
import { getAuthedSocket } from "@/realtime/getSocket";
import { AdminPipelineJob } from "./job-pipeline";
import { AdminPipelineCandidate } from "./candidate-pipeline";
import {
  Billing,
  BudgetType,
  CandidateApplicationStatus,
  Contract,
  Offer,
} from "@/types";

export interface AdminJobCandidateDetails extends AdminPipelineCandidate {
  candidateStatusId: string;
  opportunityStatus: CandidateApplicationStatus;
  opportunityStatusBeforeRoleClosed?: CandidateApplicationStatus;
  agreesWithScope?: boolean;
  agreesWithBudget?: boolean;
  agreesWithStartDate?: boolean;
  scope?: string;
  budgetType?: BudgetType;
  budgetHourly?: string;
  budgetMonthly?: string;
  startDate?: string;
  matchCriteria: string[];
  matchCriteriaQuote: string[];
  personalityRoleStrength?: string;
  personalityRoleStrengthDetail?: string;
  behaviorRoleStrength?: string;
  behaviorRoleStrengthDetail?: string;
  motivatorRoleStrength?: string;
  motivatorRoleStrengthDetail?: string;
  matchSummary?: string;
  totalOptInsSent: number;
  latestOptInRequestSentDate?: string;
  nextOptInRequestSendDate?: string;
  nextOptInRequestId?: string;
  rejectionReasons?: string[];
  optOutReasons?: string[];
  offers: Offer[];
  billing?: Billing;
  notes?: string;
}

export interface AdminJobDetails extends AdminPipelineJob {
  candidates: AdminJobCandidateDetails[];
  contracts: Contract[];
}

export const useAdminJobDetailsQuery = (openingId = "") => {
  const qc = useQueryClient();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const joinedRef = useRef(false);
  const handlerRef = useRef<
    | ((incoming: {
        id: string;
        candidates?: AdminJobCandidateDetails[];
      }) => void)
    | null
  >(null);

  const {
    data: adminJobDetails,
    error: adminJobDetailsError,
    isLoading: adminJobDetailsLoading,
  } = useQuery({
    queryKey: QueryKeys.adminJobDetails(openingId),
    enabled: !!openingId,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    async queryFn() {
      const token = await getAccessTokenSilently();
      return await request<AdminJobDetails>({
        method: "GET",
        path: `/api/admin/job_opening/${openingId}`,
        token,
      });
    },
  });

  useEffect(() => {
    if (!openingId || !isAuthenticated) {
      return;
    }

    let cancelled = false;

    void (async () => {
      if (cancelled) {
        return;
      }

      const socket = await getAuthedSocket(getAccessTokenSilently);

      if (!adminJobDetails?.candidateGenerationBlockedUntil) {
        if (joinedRef.current) {
          socket.emit("job_opening_admin:unsubscribe", openingId);
          joinedRef.current = false;
        }

        if (handlerRef.current) {
          socket.off("job_opening_admin:new_candidates", handlerRef.current);
          handlerRef.current = null;
        }

        return;
      }

      if (!joinedRef.current) {
        socket.emit("job_opening_admin:subscribe", openingId);
        joinedRef.current = true;
      }

      // Stable handler
      const onUpdate = (incoming: {
        id: string;
        candidates?: AdminJobCandidateDetails[];
      }) => {
        if (incoming.id !== openingId) {
          return;
        }

        // Update cache from payload (no refetch)
        qc.setQueryData<AdminJobDetails>(
          QueryKeys.adminJobDetails(openingId),
          (old) => {
            if (!old) return;

            const candidates = [...old.candidates];
            incoming.candidates?.forEach((candidate) => {
              const idx = candidates.findIndex(({ id }) => id === candidate.id);

              if (idx === -1) {
                candidates.push(candidate);
              } else {
                candidates[idx] = candidate;
              }
            });

            return {
              ...old,
              candidates,
              candidateCount: candidates.length,
              candidateGenerationBlockedUntil: null,
            };
          },
        );
      };

      // Replace old handler
      if (handlerRef.current) {
        socket.off("job_opening_admin:new_candidates", handlerRef.current);
      }

      handlerRef.current = onUpdate;
      socket.on("job_opening_admin:new_candidates", onUpdate);
    })();

    return () => {
      cancelled = true;

      void (async () => {
        try {
          const socket = await getAuthedSocket(getAccessTokenSilently);
          if (handlerRef.current) {
            socket.off("job_opening_admin:new_candidates", handlerRef.current);
            handlerRef.current = null;
          }
          if (joinedRef.current) {
            socket.emit("job_opening_admin:unsubscribe", openingId);
            joinedRef.current = false;
          }
        } catch {
          /* ignore */
        }
      })();
    };
  }, [isAuthenticated, openingId, adminJobDetails, qc, getAccessTokenSilently]);

  return {
    adminJobDetails,
    adminJobDetailsError,
    adminJobDetailsLoading,
  };
};
